export type SoundCategory = 'soft' | 'balanced' | 'ringing' | 'alert' | 'custom'

export interface SoundTone {
  at: number
  frequency: number
  duration: number
  volume: number
  wave: OscillatorType
  endFrequency?: number
}

interface SoundPresetBase {
  id: string
  name: string
  category: SoundCategory
  character: string
}

interface GeneratedSoundPreset extends SoundPresetBase {
  tones: SoundTone[]
}

interface AudioSoundPreset extends SoundPresetBase {
  audioUrl: string
  duration: number
}

export type SoundPreset = GeneratedSoundPreset | AudioSoundPreset

interface SequenceOptions {
  duration?: number
  gap?: number
  volume?: number
  wave?: OscillatorType
  endFrequencyRatio?: number
}

const sequence = (
  frequencies: number[],
  {
    duration = 0.16,
    gap = 0.06,
    volume = 0.24,
    wave = 'sine',
    endFrequencyRatio,
  }: SequenceOptions = {},
): SoundTone[] =>
  frequencies.map((frequency, index) => ({
    at: index * (duration + gap),
    frequency,
    duration,
    volume,
    wave,
    ...(endFrequencyRatio ? { endFrequency: frequency * endFrequencyRatio } : {}),
  }))

const chord = (
  frequencies: number[],
  duration = 0.34,
  volume = 0.13,
  wave: OscillatorType = 'sine',
): SoundTone[] => frequencies.map(frequency => ({ at: 0, frequency, duration, volume, wave }))

const tone = (
  frequency: number,
  at: number,
  duration: number,
  volume: number,
  wave: OscillatorType = 'sine',
  endFrequency?: number,
): SoundTone => ({
  frequency,
  at,
  duration,
  volume,
  wave,
  ...(endFrequency ? { endFrequency } : {}),
})

export const SOUND_CATEGORY_META: Record<
  SoundCategory,
  { label: string; description: string; color: string }
> = {
  soft: { label: 'Мягкие', description: 'Спокойные и низкие', color: '#3f8f72' },
  balanced: { label: 'Сбалансированные', description: 'Универсальные', color: '#5271a8' },
  ringing: { label: 'Звонкие', description: 'Чёткие и лёгкие', color: '#b2792b' },
  alert: { label: 'Акцентные', description: 'Хорошо заметные', color: '#b55462' },
  custom: { label: 'Кастомные', description: 'Загруженные файлы', color: '#68717d' },
}

export const SOUND_CATEGORY_ORDER: SoundCategory[] = [
  'soft',
  'balanced',
  'ringing',
  'alert',
  'custom',
]

export const SOUND_PRESETS: SoundPreset[] = [
  {
    id: 'calm-beacon',
    name: 'Спокойный маяк',
    category: 'soft',
    character: 'Тёплый двойной',
    tones: sequence([262, 392], { duration: 0.19, gap: 0.09, volume: 0.25 }),
  },
  {
    id: 'low-ripple',
    name: 'Низкая рябь',
    category: 'soft',
    character: 'Плавный нисходящий',
    tones: sequence([294, 247, 220], { duration: 0.14, gap: 0.025, volume: 0.2 }),
  },
  {
    id: 'velvet-chord',
    name: 'Бархатный аккорд',
    category: 'soft',
    character: 'Мягкий объёмный',
    tones: chord([196, 247, 294], 0.42, 0.1, 'sine'),
  },
  {
    id: 'evening-signal',
    name: 'Вечерний сигнал',
    category: 'soft',
    character: 'Низкий короткий',
    tones: sequence([220, 277], { duration: 0.22, gap: 0.08, volume: 0.23 }),
  },
  {
    id: 'wooden-tap',
    name: 'Деревянный щелчок',
    category: 'soft',
    character: 'Сухой приглушённый',
    tones: sequence([180, 150], {
      duration: 0.09,
      gap: 0.045,
      volume: 0.2,
      wave: 'triangle',
      endFrequencyRatio: 0.72,
    }),
  },
  {
    id: 'horizon',
    name: 'Горизонт',
    category: 'soft',
    character: 'Плавный подъём',
    tones: sequence([196, 220, 262], { duration: 0.17, gap: 0.035, volume: 0.19 }),
  },
  {
    id: 'muted-bell',
    name: 'Приглушённый колокол',
    category: 'soft',
    character: 'Один глубокий',
    tones: chord([165, 330], 0.46, 0.12, 'sine'),
  },
  {
    id: 'deep-drop',
    name: 'Глубокая капля',
    category: 'soft',
    character: 'Короткий с понижением',
    tones: sequence([260], {
      duration: 0.34,
      volume: 0.24,
      wave: 'sine',
      endFrequencyRatio: 0.58,
    }),
  },
  {
    id: 'clean-signal',
    name: 'Чистый сигнал',
    category: 'balanced',
    character: 'Короткий нейтральный',
    tones: sequence([330, 440], { duration: 0.13, gap: 0.055, volume: 0.22 }),
  },
  {
    id: 'two-step',
    name: 'Два шага',
    category: 'balanced',
    character: 'Ровный двойной',
    tones: sequence([349, 392], { duration: 0.15, gap: 0.08, volume: 0.22 }),
  },
  {
    id: 'compass',
    name: 'Компас',
    category: 'balanced',
    character: 'Уверенный подъём',
    tones: sequence([294, 370, 440], { duration: 0.115, gap: 0.035, volume: 0.19 }),
  },
  {
    id: 'round-pulse',
    name: 'Круглый импульс',
    category: 'balanced',
    character: 'Плотный одиночный',
    tones: chord([262, 393], 0.28, 0.14, 'triangle'),
  },
  {
    id: 'warm-glass',
    name: 'Тёплое стекло',
    category: 'balanced',
    character: 'Мягкий звон',
    tones: sequence([392, 523], { duration: 0.18, gap: 0.075, volume: 0.18 }),
  },
  {
    id: 'office-call',
    name: 'Офисный вызов',
    category: 'balanced',
    character: 'Чёткий деловой',
    tones: sequence([330, 415, 330], { duration: 0.105, gap: 0.045, volume: 0.2 }),
  },
  {
    id: 'soft-ascent',
    name: 'Мягкий подъём',
    category: 'balanced',
    character: 'Спокойный тройной',
    tones: sequence([262, 330, 392], { duration: 0.14, gap: 0.04, volume: 0.18 }),
  },
  {
    id: 'steady-mark',
    name: 'Ровная отметка',
    category: 'balanced',
    character: 'Двойной одинаковый',
    tones: sequence([370, 370], { duration: 0.13, gap: 0.11, volume: 0.21 }),
  },
  {
    id: 'crystal',
    name: 'Кристалл',
    category: 'ringing',
    character: 'Аккорд с мерцанием',
    tones: [tone(523, 0, 0.34, 0.1), tone(784, 0, 0.24, 0.07), tone(1046, 0.045, 0.18, 0.04)],
  },
  {
    id: 'silver-bell',
    name: 'Серебряный звон',
    category: 'ringing',
    character: 'Удар и светлый ответ',
    tones: [
      tone(587, 0, 0.13, 0.14, 'triangle'),
      tone(880, 0.19, 0.24, 0.1),
      tone(1174, 0.21, 0.14, 0.035),
    ],
  },
  {
    id: 'bright-double',
    name: 'Яркая пара',
    category: 'ringing',
    character: 'Два удара с хвостом',
    tones: [tone(659, 0, 0.08, 0.16), tone(784, 0.12, 0.08, 0.16), tone(988, 0.17, 0.18, 0.055)],
  },
  {
    id: 'clear-line',
    name: 'Чистая линия',
    category: 'ringing',
    character: 'Один восходящий слайд',
    tones: [tone(440, 0, 0.36, 0.17, 'sine', 660)],
  },
  {
    id: 'prism',
    name: 'Призма',
    category: 'ringing',
    character: 'Арпеджио с финалом',
    tones: [
      tone(523, 0, 0.09, 0.13),
      tone(659, 0.07, 0.09, 0.13),
      tone(880, 0.14, 0.23, 0.09),
      tone(1046, 0.15, 0.15, 0.03),
    ],
  },
  {
    id: 'air-chime',
    name: 'Воздушный перезвон',
    category: 'ringing',
    character: 'Раскрывающийся аккорд',
    tones: [
      tone(440, 0, 0.4, 0.075),
      tone(554, 0.035, 0.34, 0.07),
      tone(659, 0.075, 0.29, 0.065),
      tone(880, 0.17, 0.15, 0.035),
    ],
  },
  {
    id: 'triple-spark',
    name: 'Три искры',
    category: 'ringing',
    character: 'Синкопированный ритм',
    tones: [tone(659, 0, 0.055, 0.17), tone(740, 0.07, 0.055, 0.15), tone(880, 0.19, 0.09, 0.16)],
  },
  {
    id: 'morning',
    name: 'Утро',
    category: 'ringing',
    character: 'Мелодия вопрос–ответ',
    tones: [
      tone(494, 0, 0.09, 0.13),
      tone(659, 0.12, 0.1, 0.14),
      tone(494, 0.28, 0.08, 0.1),
      tone(784, 0.38, 0.19, 0.12),
    ],
  },
  {
    id: 'glass-drop',
    name: 'Стеклянная капля',
    category: 'ringing',
    character: 'Щелчок с обертоном',
    tones: [tone(698, 0, 0.18, 0.13, 'triangle'), tone(1396, 0.012, 0.11, 0.035)],
  },
  {
    id: 'opal',
    name: 'Опал',
    category: 'ringing',
    character: 'Мотив с тихим эхом',
    tones: [tone(523, 0, 0.14, 0.12), tone(698, 0.1, 0.2, 0.13), tone(523, 0.34, 0.12, 0.055)],
  },
  {
    id: 'small-bell',
    name: 'Малый колокол',
    category: 'ringing',
    character: 'Трёхслойный колокол',
    tones: [
      tone(587, 0, 0.42, 0.105, 'triangle'),
      tone(880, 0.015, 0.24, 0.05),
      tone(1174, 0.025, 0.16, 0.025),
    ],
  },
  {
    id: 'clear-cascade',
    name: 'Чистый каскад',
    category: 'ringing',
    character: 'Перекрывающийся спуск',
    tones: [tone(880, 0, 0.18, 0.11), tone(740, 0.08, 0.2, 0.12), tone(659, 0.17, 0.24, 0.12)],
  },
  {
    id: 'sun-ray',
    name: 'Солнечный луч',
    category: 'ringing',
    character: 'Крещендо в аккорд',
    tones: [
      tone(494, 0, 0.1, 0.08),
      tone(659, 0.1, 0.12, 0.1),
      tone(988, 0.21, 0.24, 0.09),
      tone(659, 0.22, 0.27, 0.055),
    ],
  },
  {
    id: 'porcelain',
    name: 'Фарфор',
    category: 'ringing',
    character: 'Слайд и короткий блик',
    tones: [tone(784, 0, 0.31, 0.13, 'sine', 565), tone(1174, 0.2, 0.09, 0.035)],
  },
  {
    id: 'fine-chime',
    name: 'Тонкий перезвон',
    category: 'ringing',
    character: 'Неровный ритмический',
    tones: [
      tone(587, 0, 0.09, 0.13),
      tone(784, 0.07, 0.12, 0.11),
      tone(659, 0.23, 0.08, 0.1),
      tone(988, 0.31, 0.19, 0.1),
    ],
  },
  {
    id: 'bell-step',
    name: 'Звонкая ступень',
    category: 'ringing',
    character: 'Лестница с разрешением',
    tones: [
      tone(554, 0, 0.08, 0.13),
      tone(740, 0.11, 0.08, 0.13),
      tone(880, 0.22, 0.22, 0.11),
      tone(440, 0.22, 0.24, 0.045),
    ],
  },
  {
    id: 'light-point',
    name: 'Световая точка',
    category: 'ringing',
    character: 'Сухой пинг',
    tones: [tone(659, 0, 0.09, 0.13, 'triangle'), tone(988, 0.012, 0.15, 0.055)],
  },
  {
    id: 'spring-ring',
    name: 'Весенний звон',
    category: 'ringing',
    character: 'Прыгающий мотив',
    tones: [
      tone(523, 0, 0.08, 0.12),
      tone(784, 0.1, 0.09, 0.14),
      tone(659, 0.21, 0.08, 0.11),
      tone(1046, 0.31, 0.16, 0.08),
    ],
  },
  {
    id: 'ice-arc',
    name: 'Ледяная дуга',
    category: 'ringing',
    character: 'Встречные скольжения',
    tones: [tone(698, 0, 0.38, 0.1, 'sine', 523), tone(440, 0.08, 0.31, 0.075, 'sine', 659)],
  },
  {
    id: 'four-beads',
    name: 'Четыре бусины',
    category: 'ringing',
    character: 'Две ритмические пары',
    tones: [
      tone(587, 0, 0.06, 0.14),
      tone(659, 0.07, 0.06, 0.13),
      tone(784, 0.23, 0.06, 0.13),
      tone(988, 0.3, 0.11, 0.12),
    ],
  },
  {
    id: 'low-warning',
    name: 'Низкое внимание',
    category: 'alert',
    character: 'Заметный низкий',
    tones: sequence([196, 196], { duration: 0.19, gap: 0.1, volume: 0.27, wave: 'triangle' }),
  },
  {
    id: 'steady-alert',
    name: 'Ровный сигнал',
    category: 'alert',
    character: 'Строгий двойной',
    tones: sequence([330, 330], { duration: 0.17, gap: 0.09, volume: 0.26, wave: 'triangle' }),
  },
  {
    id: 'two-tone-call',
    name: 'Двухтональный вызов',
    category: 'alert',
    character: 'Контрастный',
    tones: sequence([262, 494], { duration: 0.16, gap: 0.075, volume: 0.23 }),
  },
  {
    id: 'descending-alert',
    name: 'Нисходящий акцент',
    category: 'alert',
    character: 'Чёткое завершение',
    tones: sequence([523, 392, 294], { duration: 0.105, gap: 0.035, volume: 0.21 }),
  },
  {
    id: 'firm-signal',
    name: 'Уверенный сигнал',
    category: 'alert',
    character: 'Плотный тройной',
    tones: sequence([294, 370, 294], {
      duration: 0.12,
      gap: 0.045,
      volume: 0.24,
      wave: 'triangle',
    }),
  },
  {
    id: 'radar',
    name: 'Радар',
    category: 'alert',
    character: 'Короткий скользящий',
    tones: sequence([240, 360], {
      duration: 0.22,
      gap: 0.08,
      volume: 0.22,
      endFrequencyRatio: 1.18,
    }),
  },
  {
    id: 'deep-triple',
    name: 'Глубокая тройка',
    category: 'alert',
    character: 'Низкий ритмичный',
    tones: sequence([220, 220, 277], { duration: 0.11, gap: 0.07, volume: 0.25 }),
  },
  {
    id: 'priority',
    name: 'Приоритет',
    category: 'alert',
    character: 'Выраженный финал',
    tones: sequence([330, 415, 523], {
      duration: 0.13,
      gap: 0.045,
      volume: 0.23,
      wave: 'triangle',
    }),
  },
  {
    id: 'custom-notice-14',
    name: 'Notice 14',
    category: 'custom',
    character: 'Файл notice14.mp3',
    audioUrl: '/sounds/notifications/custom/notice-14.wav',
    duration: 0.548571,
  },
  {
    id: 'custom-ringing-short',
    name: 'Ringing Short',
    category: 'custom',
    character: 'Файл ringing-short-notification-sound.mp3',
    audioUrl: '/sounds/notifications/custom/ringing-short.wav',
    duration: 2.063673,
  },
  {
    id: 'custom-sound-11295',
    name: 'Sound 11295',
    category: 'custom',
    character: 'Загруженный MP3',
    audioUrl: '/sounds/notifications/custom/sound-11295.wav',
    duration: 1.17551,
  },
  {
    id: 'custom-sound-11332',
    name: 'Sound 11332',
    category: 'custom',
    character: 'Загруженный MP3',
    audioUrl: '/sounds/notifications/custom/sound-11332.wav',
    duration: 1.097143,
  },
  {
    id: 'custom-sound-11194',
    name: 'Sound 11194',
    category: 'custom',
    character: 'Загруженный MP3',
    audioUrl: '/sounds/notifications/custom/sound-11194.wav',
    duration: 1.358367,
  },
  {
    id: 'custom-sound-11333',
    name: 'Sound 11333',
    category: 'custom',
    character: 'Загруженный MP3',
    audioUrl: '/sounds/notifications/custom/sound-11333.wav',
    duration: 1.593469,
  },
  {
    id: 'custom-sound-11166',
    name: 'Sound 11166',
    category: 'custom',
    character: 'Загруженный MP3',
    audioUrl: '/sounds/notifications/custom/sound-11166.wav',
    duration: 1.358367,
  },
  {
    id: 'custom-sound-11330',
    name: 'Sound 11330',
    category: 'custom',
    character: 'Загруженный MP3',
    audioUrl: '/sounds/notifications/custom/sound-11330.wav',
    duration: 1.593469,
  },
  {
    id: 'custom-sound-11342',
    name: 'Sound 11342',
    category: 'custom',
    character: 'Загруженный MP3',
    audioUrl: '/sounds/notifications/custom/sound-11342.wav',
    duration: 1.515102,
  },
  {
    id: 'custom-sound-11086',
    name: 'Sound 11086',
    category: 'custom',
    character: 'Загруженный MP3',
    audioUrl: '/sounds/notifications/custom/sound-11086.wav',
    duration: 1.462857,
  },
  {
    id: 'custom-sound-11296',
    name: 'Sound 11296',
    category: 'custom',
    character: 'Загруженный MP3',
    audioUrl: '/sounds/notifications/custom/sound-11296.wav',
    duration: 1.541224,
  },
]

export const getSoundDuration = (preset: SoundPreset): number =>
  'audioUrl' in preset
    ? preset.duration
    : Math.max(...preset.tones.map(tone => tone.at + tone.duration))
